import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
// =============================================================================
// SQS Message Schemas - Contracts between Node.js and Python workers
// =============================================================================
/**
 * Job types
 */
export const JobTypeEnum = z.enum([
    'ANALYZE_DOCUMENT',
    'DOCUSIGN_SEND',
    'FETCH_LOCATION_INTELLIGENCE',
    'AGGREGATE_PROPERTY', // v4.0: Batch processing aggregation
    'GENERATE_PROPERTY_INSIGHTS', // v5.0: Property-level insight synthesis
    'FETCH_MARKET_DATA', // v8.0: Perplexity market data pipeline
    'REFRESH_INSIGHTS', // v9.x: Re-run extraction + property synthesis (PATCH analysis.json, fresh context/extraction)
    'REFRESH_SIGNATURES', // v9.x: Re-run signature detection only (PATCH analysis.json, fresh anchored.pdf)
    'EXTRACT_OFFER_TERMS', // v7 (offers): Gemini reads an uploaded PA and extracts offer terms for confirm-first pre-fill
]);
/**
 * Job completion status
 */
export const JobCompletionStatusEnum = z.enum(['SUCCESS', 'ERROR']);
// =============================================================================
// Node.js → Python: Document Analysis Job
// =============================================================================
/**
 * Document Analysis Job - Sent when a document is uploaded
 * Python worker will run Textract/Gemini and detect signature fields
 */
export const DocumentAnalysisJobSchema = z.object({
    type: z.literal('ANALYZE_DOCUMENT'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        jobId: z.string().uuid(),
        documentId: z.string(),
        organizationId: z.string(),
        propertyId: z.string(),
        /** v4.0: Optional batch reference for multi-file uploads */
        batchId: z.string().nullable().optional(),
        /** Transaction context for signature detection — party counts from property.parties[] */
        transactionContext: z.object({
            buyerCount: z.number().int().min(0).default(0),
            sellerCount: z.number().int().min(0).default(0),
        }).optional(),
        s3: z.object({
            bucket: z.string(),
            key: z.string(),
        }),
        options: z.object({
            detectSignatures: z.boolean().default(true),
            generateInsights: z.boolean().default(false),
            /** Force reprocessing even if duplicate detected */
            forceReprocess: z.boolean().default(false),
            /** v6.4: Purchase agreement mode — skip gatekeeper, use PA prompt */
            purchaseAgreementMode: z.boolean().default(false),
        }),
        /** v6.4: Offer ID for linking PA analysis back to the offer */
        offerId: z.string().optional(),
        /** AI naming: title of a new upload — set only when a name suggestion is wanted */
        documentTitle: z.string().optional(),
    }),
});
// =============================================================================
// Node.js → Python: DocuSign Send Job
// =============================================================================
/**
 * Recipient for DocuSign envelope
 */
export const DocuSignRecipientSchema = z.object({
    contactId: z.string().optional(), // Contact ID if it's a contact
    userId: z.string().optional(), // User ID if it's an agent
    email: z.string().email(),
    name: z.string(),
    role: z.string(), // BUYER_1, SELLER_1, AGENT_BUYER, etc.
});
/**
 * DocuSign Send Job - Sent when user clicks "Send to DocuSign"
 * Python worker will annotate PDF and send via DocuSign API
 */
export const DocuSignSendJobSchema = z.object({
    type: z.literal('DOCUSIGN_SEND'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        jobId: z.string().uuid(),
        organizationId: z.string(),
        propertyId: z.string(),
        userId: z.string(), // For DocuSign token lookup
        documentIds: z.array(z.string()),
        recipients: z.object({
            buyers: z.array(DocuSignRecipientSchema).default([]),
            sellers: z.array(DocuSignRecipientSchema).default([]),
            agents: z.array(DocuSignRecipientSchema).default([]),
        }),
        envelope: z.object({
            subject: z.string(),
            message: z.string().optional(),
            status: z.enum(['sent', 'created']).default('sent'), // 'created' = draft
        }),
    }),
});
// =============================================================================
// Python → Node.js: Job Completion Message
// =============================================================================
/**
 * Analysis completion result
 */
export const AnalysisCompletionResultSchema = z.object({
    documentId: z.string(),
    /** v4.0: Batch reference for multi-file uploads (passed through from job) */
    batchId: z.string().nullable().optional(),
    analysisS3Key: z.string().optional(),
    annotatedPdfS3Key: z.string().optional(),
    candidateCount: z.number().int().optional(),
    houseScore: z.number().min(0).max(100).optional(),
    // Deduplication fields
    /** SHA-256 hash of the original PDF file */
    fileHash: z.string().optional(),
    /** True if this was a duplicate (analysis copied from existing) */
    isDuplicate: z.boolean().optional(),
    /** If duplicate, the source document ID whose analysis was copied */
    sourceDocumentId: z.string().optional(),
    /** v6.3: Size in bytes after PDF optimization (set when bloated scan was compressed) */
    optimizedSizeBytes: z.number().int().min(0).optional(),
    /** v6.4: Offer ID — set when this analysis is for a purchase agreement attached to an offer */
    offerId: z.string().optional(),
    /** v6.4: Detected form family from keyword scan of first 3 pages (CAR/PRDS/SFAR) */
    detectedFormFamily: z.string().optional(),
    /** AI naming: full standard name suggested for a new upload */
    suggestedTitle: z.string().optional(),
    /** AI naming: the title the suggestion was based on (apply only if unchanged) */
    suggestedFromTitle: z.string().optional(),
});
/**
 * DocuSign completion result
 */
export const DocuSignCompletionResultSchema = z.object({
    envelopeId: z.string(),
    envelopeStatus: z.string(),
    documentIds: z.array(z.string()),
});
/**
 * Location Intelligence completion result (v3.3)
 * Returned when FETCH_LOCATION_INTELLIGENCE job completes
 */
export const LocationScoresResultSchema = z.object({
    schools: z.number().int().min(0).max(100).nullable().optional(),
    safety: z.number().int().min(0).max(100).nullable().optional(),
    neighborhood: z.number().int().min(0).max(100).nullable().optional(),
    investment: z.number().int().min(0).max(100).nullable().optional(),
});
export const NeighborhoodInfoResultSchema = z.object({
    locationDemographics: z.string().nullable().optional(),
    schoolsEducation: z.string().nullable().optional(),
    amenitiesTransportation: z.string().nullable().optional(),
    safetyInfo: z.string().nullable().optional(), // v3.6.1
    investmentInfo: z.string().nullable().optional(), // v3.6.1
});
export const LocationIntelligenceCompletionResultSchema = z.object({
    propertyId: z.string(),
    locationScores: LocationScoresResultSchema.nullable().optional(),
    neighborhoodSummary: z.string().nullable().optional(),
    neighborhoodInfo: NeighborhoodInfoResultSchema.nullable().optional(),
});
// =============================================================================
// v4.0: Property Aggregation (Batch Processing)
// =============================================================================
/**
 * Aggregate Property Job - Sent when all documents in a batch are processed
 * Triggers property-level context and analysis aggregation
 */
export const AggregatePropertyJobSchema = z.object({
    type: z.literal('AGGREGATE_PROPERTY'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        jobId: z.string().uuid(),
        propertyId: z.string(),
        organizationId: z.string(),
        batchId: z.string(),
    }),
});
/**
 * Aggregate Property completion result
 */
export const AggregatePropertyCompletionResultSchema = z.object({
    propertyId: z.string(),
    batchId: z.string(),
    /** S3 key for aggregated property context (for chat) */
    propertyContextS3Key: z.string().optional(),
    /** S3 key for aggregated property analysis (for insights) */
    propertyAnalysisS3Key: z.string().optional(),
    /** Number of documents aggregated */
    documentCount: z.number().int().optional(),
    /** Aggregated property score (average of document scores) */
    propertyScore: z.number().int().min(0).max(100).nullable().optional(),
});
// =============================================================================
// v5.0: Property Insights Synthesis (Decoupled Pipelines)
// =============================================================================
/**
 * Generate Property Insights Job - Sent when all documents in a batch complete extraction
 *
 * v5.0: This is the "Synthesis" phase that generates unified property-level insights
 * from all document extractions using a SINGLE Gemini call instead of N calls.
 *
 * Triggered by: "Last Man Standing" pattern in completion handler
 * Input: All document IDs that completed extraction successfully
 * Output: property-analysis.json with unified insights and houseScore
 */
export const GeneratePropertyInsightsJobSchema = z.object({
    type: z.literal('GENERATE_PROPERTY_INSIGHTS'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        /** Unique job identifier */
        jobId: z.string().uuid(),
        /** Property to synthesize insights for */
        propertyId: z.string(),
        /** Organization that owns the property */
        organizationId: z.string(),
        /** Optional batch reference (null for incremental single uploads) */
        batchId: z.string().nullable().optional(),
        /** Document IDs to include in synthesis (only READY documents) */
        documentIds: z.array(z.string()).min(1, 'At least one document required for synthesis'),
        /** v9.x: Refresh batch ID if this synthesis was triggered by a refresh.
         *  Worker echoes this back in the completion result so the completion handler
         *  can correlate to the refreshState lock on the property. */
        refreshBatchId: z.string().optional(),
    }),
});
/**
 * Property Insights completion result (v5.0)
 *
 * Returned when GENERATE_PROPERTY_INSIGHTS job completes.
 * Contains unified insights synthesized from all documents.
 */
export const PropertyInsightsCompletionResultSchema = z.object({
    /** Property that was analyzed */
    propertyId: z.string(),
    /** Batch reference if this was triggered by batch completion */
    batchId: z.string().nullable().optional(),
    /** v9.x: Refresh batch reference if this synthesis was triggered by a refresh.
     *  Echoed back from the job payload so the completion handler can clear the refreshState lock. */
    refreshBatchId: z.string().optional(),
    /** S3 key for property-analysis.json containing full insights */
    propertyAnalysisS3Key: z.string().optional(),
    /** Unified house condition score (1-100) synthesized from all documents */
    houseScore: z.number().int().min(0).max(100).nullable().optional(),
    /** Number of documents included in synthesis */
    documentCount: z.number().int().min(0).optional(),
    /** Number of critical issues found across all documents */
    criticalIssueCount: z.number().int().min(0).optional(),
    /** Number of positive highlights found across all documents */
    positiveCount: z.number().int().min(0).optional(),
});
// =============================================================================
// v8.0: Market Data Job (Perplexity Agent API)
// =============================================================================
/**
 * FETCH_MARKET_DATA job — fetches public market data via Perplexity deep research.
 * Triggered at property creation (needs only address).
 */
export const FetchMarketDataJobSchema = z.object({
    type: z.literal('FETCH_MARKET_DATA'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        /** Unique job identifier */
        jobId: z.string().uuid(),
        /** Property to fetch market data for */
        propertyId: z.string(),
        /** Organization that owns the property */
        organizationId: z.string(),
        /** Property address for Perplexity research */
        address: z.object({
            street: z.string(),
            unit: z.string().optional(),
            city: z.string(),
            state: z.string(),
            zipCode: z.string(),
        }),
    }),
});
/**
 * Market Data completion result (v8.0)
 */
export const MarketDataCompletionResultSchema = z.object({
    /** Property that was analyzed */
    propertyId: z.string(),
    /** S3 key for market-data.json */
    marketDataS3Key: z.string().optional(),
    /** Estimated market value from comps */
    estimatedValue: z.number().optional(),
    /** Number of comparable sales found */
    compsCount: z.number().int().min(0).optional(),
    /** Estimated monthly rental income */
    rentalEstimate: z.number().optional(),
});
// =============================================================================
// v9.x: Refresh Jobs (Two-Button Property Refresh)
// =============================================================================
/**
 * REFRESH_INSIGHTS job — re-runs document extraction + property synthesis without
 * touching signature data.
 *
 * Worker behavior:
 * - Runs ONLY the extraction pipeline (IMC + VLM + context-building).
 * - PATCHES the existing analysis.json (preserves signatureCandidates).
 * - Writes fresh context.json and extraction.json.
 * - Does NOT upload anchored.pdf (preserves existing DocuSign-ready PDF).
 *
 * The Node API dispatches one of these per document in the refresh, then a
 * single GENERATE_PROPERTY_INSIGHTS (with refreshBatchId) after the last
 * per-doc completion lands.
 */
export const RefreshInsightsJobSchema = z.object({
    type: z.literal('REFRESH_INSIGHTS'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        jobId: z.string().uuid(),
        documentId: z.string(),
        organizationId: z.string(),
        propertyId: z.string(),
        /** Unique tracker for this refresh's per-doc batch. Echoed back in completion result. */
        refreshBatchId: z.string(),
        s3: z.object({
            bucket: z.string(),
            key: z.string(),
        }),
        /** Transaction context for signature/insights consistency — reuses ANALYZE_DOCUMENT shape. */
        transactionContext: z.object({
            buyerCount: z.number().int().min(0).default(0),
            sellerCount: z.number().int().min(0).default(0),
        }).optional(),
    }),
});
/**
 * REFRESH_SIGNATURES job — re-runs signature detection without touching extraction
 * or chat context.
 *
 * Worker behavior:
 * - Runs ONLY the signature pipeline (gatekeeper + Gemini detection + anchor placement).
 * - PATCHES the existing analysis.json (preserves propertyAddress, pipelineStats, insights).
 * - Writes fresh anchored.pdf.
 * - Does NOT touch context.json or extraction.json (preserves chat + insights data).
 */
export const RefreshSignaturesJobSchema = z.object({
    type: z.literal('REFRESH_SIGNATURES'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        jobId: z.string().uuid(),
        documentId: z.string(),
        organizationId: z.string(),
        propertyId: z.string(),
        refreshBatchId: z.string(),
        s3: z.object({
            bucket: z.string(),
            key: z.string(),
        }),
        transactionContext: z.object({
            buyerCount: z.number().int().min(0).default(0),
            sellerCount: z.number().int().min(0).default(0),
        }).optional(),
        /** Mirrors AnalyzeDocument: when the doc is a purchase agreement, use the PA prompt path. */
        purchaseAgreementMode: z.boolean().default(false),
    }),
});
/**
 * Refresh completion result (per-doc).
 *
 * v9.x: Used for both REFRESH_INSIGHTS and REFRESH_SIGNATURES per-doc completions.
 * The worker echoes refreshBatchId so the Node completion handler can correlate
 * to the property.refreshState lock. Other fields mirror AnalysisCompletionResult
 * (only the fields that the mode actually produced will be populated).
 */
export const RefreshCompletionResultSchema = z.object({
    documentId: z.string(),
    /** Echoed from the job payload — used by completion handler for Last Man Standing. */
    refreshBatchId: z.string(),
    /** S3 key for analysis.json (always written, patched in partial modes). */
    analysisS3Key: z.string().optional(),
    /** S3 key for anchored.pdf (only set in REFRESH_SIGNATURES). */
    annotatedPdfS3Key: z.string().optional(),
    /** Signature candidate count (only set in REFRESH_SIGNATURES). */
    candidateCount: z.number().int().optional(),
});
// =============================================================================
// v7 (offers): Terra Offer-Term Extraction
// =============================================================================
/**
 * EXTRACT_OFFER_TERMS job — buyer agent has uploaded a PA to the temp S3 path.
 * Worker reads the PDF, calls Gemini Flash with structured extraction prompt,
 * stores the result in Redis keyed by jobId. API serves the result back to the
 * confirm-first modal in the offer submission form.
 */
export const ExtractOfferTermsJobSchema = z.object({
    type: z.literal('EXTRACT_OFFER_TERMS'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        /** Unique job identifier — used as the Redis result key suffix. */
        jobId: z.string().uuid(),
        /** S3 key of the uploaded PA (presign-created temp key). */
        s3Key: z.string(),
        /** Caller's org (for audit + tenant scoping). */
        organizationId: z.string(),
        /** Caller's user id when authenticated; share's owner org when guest. */
        requestedBy: z.string().optional(),
    }),
});
/**
 * Per-field extraction result with confidence (0..1).
 * Worker sets value=null + confidence<0.5 when uncertain.
 */
const ConfidenceFieldNumber = z.object({
    value: z.number().nullable(),
    confidence: z.number().min(0).max(1),
});
const ConfidenceFieldString = z.object({
    value: z.string().nullable(),
    confidence: z.number().min(0).max(1),
});
export const OfferTermsExtractionSchema = z.object({
    offerAmount: ConfidenceFieldNumber.optional(),
    earnestMoney: z.object({
        value: z.number().nullable(),
        mode: z.enum(['PERCENT', 'FLAT']).nullable(),
        confidence: z.number().min(0).max(1),
    }).optional(),
    earnestMoneyDays: ConfidenceFieldNumber.optional(),
    financingType: z.object({
        value: z.enum(['CASH', 'CONVENTIONAL', 'FHA', 'VA', 'OTHER']).nullable(),
        confidence: z.number().min(0).max(1),
    }).optional(),
    loanAmount: ConfidenceFieldNumber.optional(),
    closingDate: ConfidenceFieldString.optional(), // ISO yyyy-mm-dd
    daysToCloseEscrow: ConfidenceFieldNumber.optional(),
    commission: z.object({
        amount: z.number().nullable(),
        mode: z.enum(['PERCENT', 'FLAT']).nullable(),
        paidBy: z.enum(['SELLER', 'BUYER', 'WAIVED']).nullable(),
        confidence: z.number().min(0).max(1),
    }).optional(),
    contingencies: z.object({
        financeDays: ConfidenceFieldNumber.optional(),
        appraisalDays: ConfidenceFieldNumber.optional(),
        inspectionDays: ConfidenceFieldNumber.optional(),
        homeSaleDays: ConfidenceFieldNumber.optional(),
        nonContingent: z.object({
            value: z.boolean().nullable(),
            confidence: z.number().min(0).max(1),
        }).optional(),
    }).optional(),
    specialTerms: ConfidenceFieldString.optional(),
    /** Overall confidence — typically min(per-field confidences). UI uses this to gate Apply. */
    overallConfidence: z.number().min(0).max(1),
    /**
     * Diagnostic flag: true when the document appears to be a *blank* RPA
     * template (no offerAmount, no earnestMoney, no commission, etc. AND
     * overallConfidence == 0). The dialog uses this to swap a generic "low
     * confidence" warning for a clearer message about uploading a filled RPA.
     */
    looksUnfilled: z.boolean().optional(),
    /**
     * Vision-emitted diagnostic (v8): the model saw a blank RPA template.
     * Redundant with looksUnfilled (which trips when this is true) but
     * carried separately so the frontend can distinguish "model said
     * template" from "heuristic said empty".
     */
    templateOnly: z.boolean().optional(),
    /**
     * Vision-emitted diagnostic (v8): the model saw a document that isn't
     * a purchase agreement at all (POF, pre-approval, disclosure, etc.).
     * The dialog surfaces a red banner directing the user to upload their
     * actual RPA.
     */
    notPurchaseAgreement: z.boolean().optional(),
});
export const ExtractOfferTermsCompletionResultSchema = z.object({
    /** Echoed from the job payload. */
    jobId: z.string().uuid(),
    /** Echoed s3Key — useful for cross-reference. */
    s3Key: z.string(),
    /** The structured extraction. Absent when the worker hits an unrecoverable error. */
    extraction: OfferTermsExtractionSchema.optional(),
});
/**
 * Job Completion Message - Sent by Python when processing is done
 */
export const JobCompletionMessageSchema = z.object({
    type: z.literal('JOB_COMPLETE'),
    version: z.string().default('1.0'),
    timestamp: z.string().datetime(),
    payload: z.object({
        jobId: z.string().uuid(),
        jobType: JobTypeEnum,
        status: JobCompletionStatusEnum,
        error: z
            .object({
            code: z.string(),
            message: z.string(),
            stack: z.string().optional(),
        })
            .optional(),
        result: z.union([
            AnalysisCompletionResultSchema,
            DocuSignCompletionResultSchema,
            LocationIntelligenceCompletionResultSchema,
            AggregatePropertyCompletionResultSchema, // v4.0: Batch aggregation
            PropertyInsightsCompletionResultSchema, // v5.0: Property-level synthesis
            MarketDataCompletionResultSchema, // v8.0: Perplexity market data
            RefreshCompletionResultSchema, // v9.x: REFRESH_INSIGHTS / REFRESH_SIGNATURES per-doc result
            ExtractOfferTermsCompletionResultSchema, // v7 (offers): Terra pre-fill extraction
        ]).optional(),
    }),
});
// =============================================================================
// Helper Functions
// =============================================================================
/**
 * Create a document analysis job message
 */
export function createAnalysisJob(params) {
    return {
        type: 'ANALYZE_DOCUMENT',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: uuidv4(),
            documentId: params.documentId,
            organizationId: params.organizationId,
            propertyId: params.propertyId,
            batchId: params.batchId ?? null,
            transactionContext: params.transactionContext,
            s3: {
                bucket: params.bucket,
                key: params.key,
            },
            options: {
                detectSignatures: params.options?.detectSignatures ?? true,
                generateInsights: params.options?.generateInsights ?? false,
                forceReprocess: params.options?.forceReprocess ?? false,
                purchaseAgreementMode: params.options?.purchaseAgreementMode ?? false,
            },
            offerId: params.offerId,
            documentTitle: params.documentTitle,
        },
    };
}
/**
 * Create a DocuSign send job message
 */
export function createDocuSignJob(params) {
    return {
        type: 'DOCUSIGN_SEND',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: uuidv4(),
            organizationId: params.organizationId,
            propertyId: params.propertyId,
            userId: params.userId,
            documentIds: params.documentIds,
            recipients: params.recipients,
            envelope: params.envelope,
        },
    };
}
/**
 * Create an aggregate property job message
 * v4.0: Triggered when all documents in a batch are processed
 */
export function createAggregatePropertyJob(params) {
    return {
        type: 'AGGREGATE_PROPERTY',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: uuidv4(),
            propertyId: params.propertyId,
            organizationId: params.organizationId,
            batchId: params.batchId,
        },
    };
}
/**
 * Create a property insights synthesis job message
 *
 * v5.0: Triggered when all documents in a batch complete extraction.
 * This generates unified property-level insights from ALL document extractions
 * using a single Gemini call instead of N separate calls.
 *
 * @param params.propertyId - Property to synthesize insights for
 * @param params.organizationId - Organization that owns the property
 * @param params.batchId - Optional batch reference (null for incremental uploads)
 * @param params.documentIds - Document IDs to include (only READY documents)
 * @returns GeneratePropertyInsightsJob message ready to send to SQS
 *
 * @example
 * ```typescript
 * const job = createPropertyInsightsJob({
 *   propertyId: 'prop_123',
 *   organizationId: 'org_456',
 *   batchId: 'batch_789',
 *   documentIds: ['doc_1', 'doc_2', 'doc_3'],
 * });
 * await sqsClient.send(new SendMessageCommand({
 *   QueueUrl: ANALYSIS_QUEUE_URL,
 *   MessageBody: JSON.stringify(job),
 * }));
 * ```
 */
export function createPropertyInsightsJob(params) {
    // Validate at least one document is provided
    if (!params.documentIds || params.documentIds.length === 0) {
        throw new Error('createPropertyInsightsJob requires at least one documentId');
    }
    return {
        type: 'GENERATE_PROPERTY_INSIGHTS',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: uuidv4(),
            propertyId: params.propertyId,
            organizationId: params.organizationId,
            batchId: params.batchId ?? null,
            documentIds: params.documentIds,
            refreshBatchId: params.refreshBatchId,
        },
    };
}
// =============================================================================
// v9.x: Refresh Job Factories
// =============================================================================
/**
 * Create a REFRESH_INSIGHTS job message.
 *
 * v9.x: Triggered by the "Refresh insights" button on a property. The worker
 * re-runs only the extraction pipeline (skips signature detection) and
 * PATCHES the existing analysis.json so signatureCandidates / anchored.pdf
 * are preserved.
 */
export function createRefreshInsightsJob(params) {
    return {
        type: 'REFRESH_INSIGHTS',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: uuidv4(),
            documentId: params.documentId,
            organizationId: params.organizationId,
            propertyId: params.propertyId,
            refreshBatchId: params.refreshBatchId,
            s3: {
                bucket: params.bucket,
                key: params.key,
            },
            transactionContext: params.transactionContext,
        },
    };
}
/**
 * Create a REFRESH_SIGNATURES job message.
 *
 * v9.x: Triggered by the "Refresh signature detection" button on a property.
 * The worker re-runs only the signature pipeline (skips extraction) and
 * PATCHES the existing analysis.json so insights / context.json / extraction.json
 * are preserved.
 */
export function createRefreshSignaturesJob(params) {
    return {
        type: 'REFRESH_SIGNATURES',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: uuidv4(),
            documentId: params.documentId,
            organizationId: params.organizationId,
            propertyId: params.propertyId,
            refreshBatchId: params.refreshBatchId,
            s3: {
                bucket: params.bucket,
                key: params.key,
            },
            transactionContext: params.transactionContext,
            purchaseAgreementMode: params.purchaseAgreementMode ?? false,
        },
    };
}
/**
 * Create a FETCH_MARKET_DATA job message.
 *
 * Fetches public market data (comps, schools, rental model, investment analysis)
 * via Perplexity Agent API deep research.
 */
export function createMarketDataJob(params) {
    return {
        type: 'FETCH_MARKET_DATA',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: params.jobId ?? uuidv4(),
            propertyId: params.propertyId,
            organizationId: params.organizationId,
            address: params.address,
        },
    };
}
/**
 * Create an EXTRACT_OFFER_TERMS job. v7 (offers).
 *
 * The API caller atomically claims the extraction via Redis SETNX on
 * `offer-extract-claim:{s3Key}`, generates a UUID, queues this job, then
 * polls `offer-extract:{jobId}` until the worker writes the result.
 */
export function createExtractOfferTermsJob(params) {
    return {
        type: 'EXTRACT_OFFER_TERMS',
        version: '1.0',
        timestamp: new Date().toISOString(),
        payload: {
            jobId: params.jobId ?? uuidv4(),
            s3Key: params.s3Key,
            organizationId: params.organizationId,
            requestedBy: params.requestedBy,
        },
    };
}
//# sourceMappingURL=sqs-messages.js.map